import Brands from '@/components/brands';
import FreeTrialModal from '@/components/free-trial-modal';
import LocaleSwitcher from '@/components/locale-switcher';
import LogoLink from '@/components/logo-link';
import { useI18n } from '@/lib/i18n';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Instagram from '@images/Instagram.svg';
import TikTok from '@images/TikTok.svg';
import { Head, Link, usePage } from '@inertiajs/react';
import { ReactNode, useState } from 'react';

export default function MainLayout({ absoluteHeader = false, children }: { absoluteHeader?: boolean; children: ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { url, props } = usePage();
  const { t } = useI18n();

  const navigation = isSubdomain
    ? []
    : [
        { name: 'Instagram', href: route('instagram'), image: Instagram },
        { name: 'TikTok', href: route('tiktok'), image: TikTok },
        { name: t('layout.Blog'), href: route('posts.index') },
        { name: t('layout.About us'), href: route('about') },
        { name: t('layout.Help'), href: route('help') },
      ];

  const isRoute = route().current() !== undefined;

  return (
    <>
      {isSubdomain ? (
        <>
          <Head>
            <title>{props.meta.title}</title>
            <meta name="description" content={props.meta.description} />
            <meta name="robots" content="noindex, follow" />
          </Head>
        </>
      ) : (
        <Head>
          <title>{props.meta.title}</title>
          <meta name="description" content={props.meta.description} />
          {!props.meta.index && <meta name="robots" content="noindex, follow" />}

          <meta itemProp="name" content={props.meta.title} />
          <meta itemProp="description" content={props.meta.description} />
          <meta itemProp="image" content={props.meta.image} />

          <meta property="og:logo" content="/Logo.png" />
          <meta property="og:url" content={props.meta.url} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={props.meta.title} />
          <meta property="og:description" content={props.meta.description} />
          <meta property="og:image" content={props.meta.image} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={props.meta.title} />
          <meta name="twitter:description" content={props.meta.description} />
          <meta name="twitter:image" content={props.meta.image} />

          <script type="application/ld+json">{props.meta.jsonLd}</script>

          {isRoute && <link rel="canonical" href={props.meta.url} />}

          {props.supportedLocales.map(
            (locale) => isRoute && <link key={locale.key} rel="alternate" href={locale.alternateUrl} hrefLang={locale.key} />,
          )}
        </Head>
      )}

      {!isSubdomain && <FreeTrialModal />}

      <div className="border-secondary overflow-x-hidden border-t-4 bg-white">
        <header className={`inset-x-0 top-0 z-50 mx-auto max-w-screen-lg ${absoluteHeader ? 'absolute' : 'relative -mt-1'}`}>
          <nav className="flex items-center justify-between p-6 lg:px-8">
            <div className="flex lg:flex-1">
              <LogoLink />
            </div>

            <div className="flex lg:hidden">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              >
                <Bars3Icon className="size-6" />
              </button>
            </div>

            <div className="hidden lg:flex lg:gap-x-12">
              {navigation.map((item, n) => (
                <Link
                  key={n}
                  href={item.href}
                  className={`flex flex-row items-center font-semibold text-gray-900 hover:underline ${url.startsWith(item.href) ? 'underline' : ''}`}
                >
                  {item.image && <img src={item.image} alt={item.name} className={'mr-1.5 inline size-4'} />}
                  {item.name}
                </Link>
              ))}

              <LocaleSwitcher />
            </div>
          </nav>

          <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
            <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
              <div className="flex items-center justify-between">
                <LogoLink />
                <button type="button" onClick={() => setMobileMenuOpen(false)} className="-m-3 rounded-md p-2.5 text-gray-700">
                  <XMarkIcon className="size-6" />
                </button>
              </div>

              <div className="mt-6 flow-root">
                <div className="-my-6 divide-y divide-gray-500/10">
                  <div className="space-y-2 py-6">
                    {navigation.map((item, n) => (
                      <Link
                        key={n}
                        href={item.href}
                        className={`-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 ${url.startsWith(item.href) ? 'underline' : ''}`}
                      >
                        {item.name}
                      </Link>
                    ))}

                    <LocaleSwitcher />
                  </div>
                </div>
              </div>
            </DialogPanel>
          </Dialog>
        </header>

        {children}
      </div>

      <footer className={`${isSubdomain ? '' : 'border-t'} border-gray-200 bg-white p-6 sm:p-12`}>
        <div className="mx-auto max-w-screen-lg">
          {!isSubdomain && (
            <div className="md:flex">
              <div className="md:w-6/12 md:pr-16">
                <div className="flex">
                  <LogoLink large={true} />
                </div>

                <div className={'mt-4 space-y-4 text-gray-600'}>
                  <p>{t('layout.description.0')}</p>
                  <p>{t('layout.description.1')}</p>
                </div>

                <Brands showReviews={true} className={'mt-4'} />
              </div>

              <div className="mt-8 md:mt-0 md:flex-1">
                <div className={'grid grid-cols-2 gap-8'}>
                  <div>
                    <h2 className="font-bold text-gray-900">Instagram</h2>
                    <ul className="mt-4 space-y-3 text-gray-600">
                      <li>
                        <Link href={route('instagram')} className="hover:underline">
                          {t('layout.All products')}
                        </Link>
                      </li>
                      <li>
                        <Link href={'#'} className="hover:underline">
                          {t('layout.Packages')} <small className={'italic'}>{t('layout.(coming soon)')}</small>
                        </Link>
                      </li>
                      <li>
                        <Link href={route('instagram.followers')} className="hover:underline">
                          {t('layout.Get Instagram followers')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('instagram.likes')} className="hover:underline">
                          {t('layout.Get Instagram likes')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('instagram.views')} className="hover:underline">
                          {t('layout.Get Instagram views')}
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h2 className="font-bold text-gray-900">Tiktok</h2>
                    <ul className="mt-4 space-y-3 text-gray-600">
                      <li>
                        <Link href={route('tiktok')} className="hover:underline">
                          {t('layout.All products')}
                        </Link>
                      </li>
                      <li>
                        <Link href={'#'} className="hover:underline">
                          {t('layout.Packages')} <small className={'italic'}>{t('layout.(coming soon)')}</small>
                        </Link>
                      </li>
                      <li>
                        <Link href={route('tiktok.followers')} className="hover:underline">
                          {t('layout.Get TikTok followers')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('tiktok.likes')} className="hover:underline">
                          {t('layout.Get TikTok likes')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('tiktok.views')} className="hover:underline">
                          {t('layout.Get TikTok views')}
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h2 className="font-bold text-gray-900">{t('layout.Free services')}</h2>
                    <ul className="mt-4 space-y-3 text-gray-600">
                      <li>
                        <Link href={route('instagram.trial')} className="hover:underline">
                          {t('layout.Free Instagram likes')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('tiktok.trial')} className="hover:underline">
                          {t('layout.Free TikTok likes')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('growthCalculator')} className="hover:underline">
                          {t('layout.Growth Calculator')}
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h2 className="font-bold text-gray-900">Boostbuddy</h2>
                    <ul className="mt-4 space-y-3 text-gray-600">
                      <li>
                        <Link href={route('posts.index')} className="hover:underline">
                          {t('layout.Blog')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('reviews')} className="hover:underline">
                          {t('layout.Reviews')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('about')} className="hover:underline">
                          {t('layout.About us')}
                        </Link>
                      </li>
                      <li>
                        <Link href={route('help')} className="hover:underline">
                          {t('layout.Help')}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="my-16 text-center sm:flex sm:items-center sm:justify-between sm:text-left md:mt-24 md:mb-0">
            <span className="flex-1 text-xs text-gray-500 md:text-sm">
              © {new Date().getFullYear()} Boostbuddy. Not affiliated with, endorsed by, or owned by Instagram®, TikTok®, Facebook®, or any other
              third-party platforms. Instagram® and TikTok® are registered trademarks of their respective owners.
            </span>

            {!isSubdomain && (
              <div className={'text-gray-500'}>
                <a href={route('cookiePolicy')} className="text-xs hover:text-gray-900 md:text-sm">
                  {t('layout.Cookie policy')}
                </a>
                <span className="mx-1.5">&middot;</span>
                <a href={route('terms')} className="text-xs hover:text-gray-900 md:text-sm">
                  {t('layout.Terms of use')}
                </a>
                <span className="mx-1.5">&middot;</span>
                <a href={route('privacyPolicy')} className="text-xs hover:text-gray-900 md:text-sm">
                  {t('layout.Privacy policy')}
                </a>
              </div>
            )}

            <div className={'mt-1 sm:mt-0 sm:ml-3'}>
              <LocaleSwitcher reversed={true} />
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
